import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { BeneModelDetailed } from "../../models/BeneModel";
import {
  WithCUToggleCommandsAndData,
  WithRecordID,
} from "../../models/shared/CommonModels";
import { useState } from "react";
import {
  BeneContrattoPutModel,
  BeneContrattoRequest,
} from "../../models/BeneContrattoModel";
import { useMutation, useQueryClient } from "react-query";
import { editBeneContratto } from "../../api/benecontratto";
import { fetchDetailedBeneQuery } from "../../utils/QueryClient";
import ErrorMessageAlertComponent from "../errors/ErrorMessageAlertComponent";
import { VenditaBeneContrattoPostModel } from "../../models/VenditaBeneContrattoModel";
import { REQUIRED_HELPER_TEXT } from "../../utils/form";
import { AxiosError } from "axios";
import { fromAxiosErrorToMessage } from "../../utils/crud";

const SalesDataFormComponent: React.FC<
  WithCUToggleCommandsAndData<BeneModelDetailed>
> = ({ initialData, open, toggleOpen }) => {
  const queryClient = useQueryClient();

  const [hideError, setHideError] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [dataVendita, setDataVendita] = useState<Dayjs | null>(
    initialData.benecontratto !== null &&
      initialData.benecontratto[0] !== null &&
      initialData.benecontratto[0].venditabenecontratto !== null &&
      initialData.benecontratto[0].venditabenecontratto.data_vendita
      ? dayjs(initialData.benecontratto[0].venditabenecontratto.data_vendita)
      : dayjs()
  );

  const [trattenuteOC, setTrattenuteOC] = useState(
    initialData.benecontratto !== undefined &&
      initialData.benecontratto[0] !== undefined &&
      initialData.benecontratto[0]?.contratto?.importo !== null
      ? initialData.benecontratto[0]?.contratto?.importo.toString()
      : "0"
  );

  const [totaleVendita, setTotaleVendita] = useState(
    initialData.benecontratto !== null &&
      initialData.benecontratto[0] !== null &&
      initialData.benecontratto[0].venditabenecontratto !== null
      ? initialData.benecontratto[0].venditabenecontratto.totale_lordo.toString()
      : "0"
  );
  const [prezzoNetto, setPrezzoNetto] = useState(
    initialData.benecontratto !== null &&
      initialData.benecontratto[0] !== null &&
      initialData.benecontratto[0].venditabenecontratto !== null
      ? initialData.benecontratto[0].venditabenecontratto.totale_netto.toString()
      : "0"
  );

  const [sellingNotes, setSellingNotes] = useState<string>(
    initialData.benecontratto !== null &&
      initialData.benecontratto[0] !== null &&
      initialData.benecontratto[0].venditabenecontratto !== null
      ? initialData.benecontratto[0].venditabenecontratto.selling_notes
      : ""
  );

  const editMutation = useMutation({
    mutationFn: (x: WithRecordID) => {
      return editBeneContratto(x);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(fetchDetailedBeneQuery);
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const closeActions = () => {
    toggleOpen();
    if (!hideError) {
      queryClient.invalidateQueries(fetchDetailedBeneQuery);
    }
  };

  const handleSubmit = () => {
    if (
      isNaN(Number(trattenuteOC?.replace(",", "."))) ||
      isNaN(Number(totaleVendita.replace(",", "."))) ||
      isNaN(Number(prezzoNetto.replace(",", ".")))
    ) {
      setErrorMessage("Controlla il formato dei parametri");
      return;
    }
    let id_contratto = initialData.benecontratto[0].contratto
      ? initialData.benecontratto[0].contratto.id
      : undefined;
    console.log(id_contratto);
    let putData = {} as BeneContrattoPutModel;
    putData.id_condizione = 6; // venduto
    putData.id_contratto = id_contratto;

    let venditabenecontratto = {} as VenditaBeneContrattoPostModel;
    venditabenecontratto.data_vendita = dataVendita ? dataVendita.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    venditabenecontratto.percentuale_trattenute_oc = Number(
      trattenuteOC?.replace(",", ".")
    );
    venditabenecontratto.totale_lordo = Number(totaleVendita.replace(",", "."));
    venditabenecontratto.totale_netto = Number(prezzoNetto.replace(",", "."));
    venditabenecontratto.selling_notes = sellingNotes;
    putData.venditabenecontratto = venditabenecontratto;

    let putRequest = {} as WithRecordID;
    let putModel = {} as BeneContrattoRequest;
    putModel.benecontratto = putData;

    putRequest.data = putModel;
    putRequest.recordID = initialData.benecontratto[0].id;

    editMutation.mutate(putRequest);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeActions}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle>Dati Vendita Bene</DialogTitle>
        <DialogContent style={{ width: 500 }}>
          <ErrorMessageAlertComponent
            error_message={errorMessage}
            hide={() => {
              setHideError(true);
            }}
            visible={!hideError}
          />

          <Typography sx={{ fontSize: "12px", color: "grey" }}>
            {REQUIRED_HELPER_TEXT}
          </Typography>
          <br />
          {
            <Box display="flex" flexDirection="column" justifyContent="center">
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Data Di Vendita *"
                    value={dataVendita}
                    onChange={(newValue) => setDataVendita(newValue)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        margin: "normal",
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>

                {/* <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  margin="normal"
                  required
                  type="number"
                  label="Trattenute %"
                  name="trattenute_in_perc"
                  size="small"
                  value={trattenuteOC}
                  onChange={(e) => {
                    setTrattenuteOC(e.target.value);
                  }}
                /> */}

                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">€</InputAdornment>
                    ),
                  }}
                  margin="normal"
                  required
                  type="number"
                  label="Prezzo di Vendita"
                  name="totale_vendita"
                  size="small"
                  value={totaleVendita}
                  onChange={(e) => {
                    setTotaleVendita(e.target.value);
                  }}
                />

                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">€</InputAdornment>
                    ),
                  }}
                  margin="normal"
                  required
                  type="number"
                  label="Prezzo Netto"
                  name="prezzo_netto"
                  size="small"
                  value={prezzoNetto}
                  onChange={(e) => {
                    setPrezzoNetto(e.target.value);
                  }}
                />

                <TextField
                  margin="normal"
                  type="text"
                  label="Note di Vendita"
                  name="sell_notes"
                  size="small"
                  rows={3}
                  multiline
                  inputProps={{ maxLength: 250 }}
                  value={sellingNotes}
                  onChange={(e) => {
                    setSellingNotes(e.target.value);
                  }}
                />
              </>
            </Box>
          }
        </DialogContent>
        <DialogActions
          sx={{
            paddingLeft: "18pt",
            paddingRight: "18pt",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ justifyContent: "flex-start" }}>
            <Button onClick={() => closeActions()}>Chiudi</Button>
          </Box>
          <Box sx={{ justifyContent: "flex-end" }}>
            <Button onClick={() => handleSubmit()} variant="contained">
              Applica
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalesDataFormComponent;
